import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '../Loader';
import { BookComparisonBlock } from '../BookComparisonBlock';
import { SubscriptionPlanSelectorV11 } from '../SubscriptionPlanSelectorV11';
import { Icon } from '../Icon';
import { FaqV2 } from '../FaqV2';
import { getUserResponse } from '../../shared/answer-helpers';
import { getBookCover } from '../../shared/book-covers';
import { CheckoutV5 } from '../CheckoutV5';
import { useSubscriptionPlans } from '../../shared/use-subscription-plans';
import { useIntersectionTracking } from '../../shared/use-intersection-tracking';
import { scrollToPlans } from '../../shared/scroll-helpers';
import { createSubscriptionForm } from '../../../lib/use-cases/create-subscription';
import { handleCreateSubscriptionError } from '../../shared/error-helpers';
import { resolveFirstStepUrl } from '../../../lib/shared/upsell-flow';
import { getCurrentPrelander } from '../../../lib/shared/prelander-helpers';
import { getMetadata } from '../../../lib/shared/ad-helpers';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { getPlanDiscountPercent } from '../../shared/plan-helpers';
import { FP_PROMO_MAIN_TYPE } from '../../../lib/shared/promotion-type';
import {
    MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN,
    MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN,
    MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN,
    MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN,
    MK_TWELVE_WEEKS_5999_WITHOUT_TRIAL_PLAN
} from '../../../lib/shared/subscription-plans';
import styles from './SaleV36.css';

const FEELINGS = [
    { title: 'Like the main character', text: 'Because you are. Now there’s a cover, a title and a plot twist or two to prove it.' },
    { title: 'Proud, in a very good way', text: 'You’ll read your own stories back and think: wow, that was me.' },
    { title: 'The one everyone asks about', text: 'Be ready for “wait, you wrote a book?” It’s going to happen.' },
    { title: 'A little bit smug', text: 'In the best way. You’ve earned it.' },
    { title: 'Still in control', text: 'Talk or type. Edit anything. Share it with the people you pick, or nobody.' }
];

const FAQ_ITEMS = [
    { question: 'What exactly am I paying for?', answer: 'Access to Boundly while your plan is active: the questions, the chapters we write from your answers, your edits, and your private link. Cancel anytime, in 2 taps from your profile.' },
    { question: 'I’m not a writer. Is that a problem?', answer: 'Not at all. You don’t have to write: you talk or type your answers, and we turn them into written pages in your voice. You can change anything later just by asking.' },
    { question: 'Will it work on my phone?', answer: 'Yes. It works on your phone: we ask one question at a time, and you answer out loud or by typing. No templates, nothing else to learn.' },
    { question: 'Who gets to read my stories?', answer: 'Only you. Your stories are private by default. If you want to share, you get one private, password-protected link that updates as you add chapters.' },
    { question: 'What happens to my stories if I cancel?', answer: 'You keep them. The stories you’ve written stay yours even if you stop, and they stay private.' },
    { question: 'How do I cancel?', answer: 'Anytime, in 2 taps from your profile.' }
];

const plansWithMainPromo = [MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN, MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN, MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN];
const plansWithoutPromo = [MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN, MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN, MK_TWELVE_WEEKS_5999_WITHOUT_TRIAL_PLAN];
const popularPlans = [MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN, MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN];

function SaleV36({
    data,
    expiryTimestamp,
    onExpirePromo,
    isExpired,
    initialPrelander,
    selectSubscription,
    selectedSubscriptionData,
    merchantData,
    setMerchantData,
    legalLinks
}) {
    const { plans, isLoading } = useSubscriptionPlans([]);
    const [isPending, setIsPending] = useState(false);
    const [activePlanData, setActivePlanData] = useState({});
    const name = getUserResponse(data.userResponses, 9114)?.text;
    const coverSrc = getBookCover(data.userResponses);
    const firstUpsellUrl = resolveFirstStepUrl(getCurrentPrelander(), merchantData.subscriptionId);

    useIntersectionTracking('.fp-block');

    const handleClose = () => {
        selectSubscription({});
        setMerchantData({});
        scrollToPlans();
    };

    useEffect(() => {
        const handleNavigation = e => {
            if (e.navigationType === 'traverse' && window.history.state?.checkoutVisible) {
                window.history.replaceState({ checkoutVisible: false }, '');
                handleClose();
            }
        };

        logEvent(EVENT_TYPES.SALE_PAGE_VISITED);

        window.navigation.addEventListener('navigate', handleNavigation);
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        return () => {
            window.navigation.removeEventListener('navigate', handleNavigation);
        };
    }, []);

    const getPlans = () => {
        const planOrder = !isExpired && data.promoType === FP_PROMO_MAIN_TYPE ? plansWithMainPromo : plansWithoutPromo;

        return planOrder
            .map(plan => plans.find(item => item.plan === plan))
            .filter(Boolean)
            .map(plan => ({ ...plan, isPopular: popularPlans.includes(plan.plan) }));
    };

    useEffect(() => {
        const newPlans = getPlans();
        const defaultPlan = newPlans.find(plan => plan.isPopular) ?? newPlans[0];

        if (defaultPlan) {
            setActivePlanData(defaultPlan);
        }
    }, [plans, isExpired]);

    const submitSubscription = async subscriptionData => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                metadata: getMetadata(window.location.search, initialPrelander, data.userResponses)
            });

            window.history.pushState({ checkoutVisible: true }, '');
            setMerchantData(newMerchantData);

            if (newMerchantData?.fbEventId) {
                logEvent(EVENT_TYPES.SALE_CHECKOUT_INITIATE, { eventId: newMerchantData.fbEventId });
            }
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const tryAgain = async (subscriptionData, subscriptionId) => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                id: subscriptionId,
                metadata: getMetadata(window.location.search, initialPrelander, data.userResponses)
            });

            setMerchantData(newMerchantData);
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const handleSubscribe = subscriptionData => {
        logEvent(EVENT_TYPES.PLAN_SELECTED, { plan: subscriptionData.plan });
        setMerchantData({});
        selectSubscription(subscriptionData);
        submitSubscription(subscriptionData);
    };

    const planList = getPlans();

    const renderPlans = () => (isLoading
        ? <Loader className={styles.loader} variant='secondary' />
        : (
            <SubscriptionPlanSelectorV11
                onSubmit={handleSubscribe}
                expiryTimestamp={expiryTimestamp}
                onExpirePromo={onExpirePromo}
                isExpired={isExpired}
                selectedSubscriptionData={activePlanData}
                selectSubscription={setActivePlanData}
                isPending={isPending}
                plans={planList}
            />
        )
    );

    if (merchantData.merchant && selectedSubscriptionData.plan) {
        return (
            <div className={styles.fixedModal}>
                <CheckoutV5
                    merchantData={merchantData}
                    subscriptionData={selectedSubscriptionData}
                    promoType={isExpired ? '' : data.promoType}
                    onClose={handleClose}
                    tryAgain={tryAgain}
                    isPending={isPending}
                    successUrl={firstUpsellUrl}
                    termsOfUseLink={legalLinks.termsOfUse}
                    defaultDiscountValue={getPlanDiscountPercent(selectedSubscriptionData)}
                    isWalletEnabled
                />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <div className={styles.content}>
                <BookComparisonBlock userResponses={data.userResponses} />
                <div id='subscription-start-block' className={styles.subscriptionStartBlock} />
                <h2 className={styles.title}>
                    {Boolean(name) && <span className={styles.accent}>{name}, </span>}
                    you’re already iconic. Let’s put it in a book.
                </h2>
                <div id={EVENT_TYPES.FIRST_PLAN_VIEWED} className='fp-block'>
                    {renderPlans()}
                </div>
                <section className={styles.feelings}>
                    <h2 className={styles.sectionTitle}>How you’ll feel</h2>
                    <ul className={styles.feelingsList}>
                        {FEELINGS.map(({ title, text }) => (
                            <li key={title} className={styles.feeling}>
                                <Icon name='checkItemFilled' className={styles.feelingIcon} />
                                <span className={styles.feelingBody}>
                                    <span className={styles.feelingTitle}>{title}</span>
                                    {text}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
                <section className={styles.promise}>
                    <div className={styles.promisePlate}>
                        <img src={coverSrc} alt='' className={styles.promiseCover} />
                        <h2 className={styles.promiseTitle}>A woman like you doesn’t leave a book unfinished</h2>
                        <p className={styles.promiseText}>You’ve got the cover, the title and the attitude. It only needs your stories. We ask, you talk, and you get to be proud of every page.</p>
                    </div>
                </section>
                <section className={styles.faq}>
                    <FaqV2 heading='Questions you’re about to ask' items={FAQ_ITEMS} />
                </section>
                <section className={styles.closing}>
                    <h2 className={styles.sectionTitle}>Page one is waiting. So are we.</h2>
                    {renderPlans()}
                </section>
            </div>
        </div>
    );
}

SaleV36.propTypes = {
    data: PropTypes.object.isRequired,
    expiryTimestamp: PropTypes.number,
    onExpirePromo: PropTypes.func.isRequired,
    isExpired: PropTypes.bool.isRequired,
    initialPrelander: PropTypes.string.isRequired,
    selectSubscription: PropTypes.func.isRequired,
    selectedSubscriptionData: PropTypes.object.isRequired,
    merchantData: PropTypes.object.isRequired,
    setMerchantData: PropTypes.func.isRequired,
    legalLinks: PropTypes.object.isRequired
};

export {
    SaleV36
};
